import PropTypes from 'prop-types'
import { useMemo } from 'react'
// @mui
import { CssBaseline, responsiveFontSizes } from '@mui/material'
import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
  StyledEngineProvider,
} from '@mui/material/styles'
//
import palette from './palette'
import shadows from './shadows'
import typography from './typography'
import GlobalStyles from './globalStyles'
import customShadows from './customShadows'

// ----------------------------------------------------------------------

const ThemeProvider = (props) => {
  const { children } = props

  const themeOptions = useMemo(
    () => ({
      palette,
      shape: { borderRadius: 6 },
      typography,
      shadows: shadows(),
      customShadows: customShadows(),
    }),
    [],
  )

  const theme = createTheme(themeOptions)
  theme.components = {
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#FFFFFF',
          fontWeight: 600,
          fontSize: '14px',
          marginBottom: '8px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          borderRadius: '10px',
          fontSize: '14px',
          fontWeight: 400,
        },
        notchedOutline: {
          borderColor: '#383838',
          borderWidth: '1px',
          borderRadius: '10px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root.Mui-focused  .MuiOutlinedInput-notchedOutline':
            {
              borderColor: '#E2CE8E',
              borderWidth: '2px',
            },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#242424',
          borderRadius: '10px !important',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          height: '52px',
          boxShadow: 'none',
          fontSize: '16px',
          fontWeight: 700,
          minWidth: '115px',
          padding: '6px 25px',
          ':hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          background:
            'linear-gradient(92.18deg, #E2CE8E 22.55%, #E8D8A4 51.31%, #E2CE8E 78.11%)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#242424',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #0612370F',
          boxShadow: '0px 0px 10px 0px #0612370F',
          borderRadius: '10px',
          padding: '8px',
          gap: '10px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 0,
          ':last-child': {
            paddingBottom: 0,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: '1px solid #0612370F',
          boxShadow: '0px 0px 10px 0px #0612370F',
          borderRadius: '10px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          boxShadow: '0px 0px 4px 0px #00000029',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 800,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          color: '#FFFFFF',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#242424',
          color: '#E8D8A4',
          fontWeight: 500,
          border: '1px solid #0612370F',
          boxShadow: '0px 0px 4px 0px #00000029',
        },
        arrow: {
          color: '#242424',
          '::before': {
            border: '1px solid #0612370F',
          },
        },
      },
    },
  }

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={responsiveFontSizes(theme)}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  )
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
}

export default ThemeProvider
